"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useContext, useMemo, useState, useEffect, useRef, useCallback } from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { AnimatePresence, motion } from "framer-motion";
import type { ApiWave } from "@/generated/models/ApiWave";
import { WaveLeaderboardTime } from "@/components/waves/leaderboard/WaveLeaderboardTime";
import { WaveLeaderboardHeader } from "@/components/waves/leaderboard/header/WaveleaderboardHeader";
import { WaveDropCreate } from "@/components/waves/leaderboard/create/WaveDropCreate";
import { WaveLeaderboardCurationDropModal } from "@/components/waves/leaderboard/create/WaveLeaderboardCurationDropModal";
import { WaveLeaderboardDrops } from "@/components/waves/leaderboard/drops/WaveLeaderboardDrops";
import { WaveLeaderboardGallery } from "@/components/waves/leaderboard/gallery/WaveLeaderboardGallery";
import { WaveLeaderboardGrid } from "@/components/waves/leaderboard/grid/WaveLeaderboardGrid";
import { isLeaderboardViewMode, type LeaderboardViewMode } from "@/components/waves/leaderboard/types";
import { useWave } from "@/hooks/useWave";
import { AuthContext } from "@/components/auth/Auth";
import { useLayout } from "./layout/LayoutContext";
import { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import useLocalPreference from "@/hooks/useLocalPreference";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import { getWaveDropEligibility } from "@/components/waves/leaderboard/dropEligibility";
import { resolveWaveSubmissionExperience, WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
interface MyStreamWaveLeaderboardProps {
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
const MyStreamWaveLeaderboard: React.FC<MyStreamWaveLeaderboardProps> = t0 => {
  const $ = _c(85);
  const {
    wave,
    onDropClick
  } = t0;
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    isMemesWave,
    isCurationWave,
    isQuorumWave,
    participation
  } = useWave(wave);
  const {
    leaderboardViewStyle
  } = useLayout();
  const t1 = wave.participation.submission_strategy ?? null;
  let t2;
  if ($[0] !== isCurationWave || $[1] !== isMemesWave || $[2] !== isQuorumWave || $[3] !== t1) {
    t2 = resolveWaveSubmissionExperience({
      isMemesWave,
      isCurationWave,
      isQuorumWave,
      submissionStrategy: t1
    });
    $[0] = isCurationWave;
    $[1] = isMemesWave;
    $[2] = isQuorumWave;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const submissionExperience = t2;
  const mountedRef = useRef(true);
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => {
      mountedRef.current = false;
    };
    t4 = [];
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  const [isCreateDropOpen, setIsCreateDropOpen] = useState(false);
  const [isMemesCreateOpen, setIsMemesCreateOpen] = useState(false);
  const [isCurationDropModalOpen, setIsCurationDropModalOpen] = useState(false);
  const [minPrice, setMinPrice] = useState(undefined);
  const [maxPrice, setMaxPrice] = useState(undefined);
  const isLoggedIn = Boolean(connectedProfile?.handle);
  const t5 = Boolean(activeProfileProxy);
  let t6;
  if ($[7] !== isCurationWave || $[8] !== isLoggedIn || $[9] !== participation || $[10] !== t5) {
    t6 = getWaveDropEligibility({
      isLoggedIn,
      isProxy: t5,
      isCurationWave,
      participation
    });
    $[7] = isCurationWave;
    $[8] = isLoggedIn;
    $[9] = participation;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const {
    canCreateDrop
  } = t6;
  const showToggleableDropInput = submissionExperience !== WaveSubmissionExperience.MEMES_LEGACY && submissionExperience !== WaveSubmissionExperience.CURATION_LEGACY && submissionExperience !== WaveSubmissionExperience.QUORUM_PROPOSAL && isCreateDropOpen;
  let t7;
  if ($[12] !== canCreateDrop || $[13] !== submissionExperience) {
    t7 = () => {
      if (!mountedRef.current) {
        return;
      }
      if (submissionExperience === WaveSubmissionExperience.MEMES_LEGACY) {
        setIsMemesCreateOpen(true);
        return;
      }
      if (submissionExperience === WaveSubmissionExperience.CURATION_LEGACY) {
        if (!canCreateDrop) {
          return;
        }
        setIsCurationDropModalOpen(true);
        return;
      }
      if (submissionExperience === WaveSubmissionExperience.QUORUM_PROPOSAL) {
        if (!canCreateDrop) {
          return;
        }
        setIsCreateDropOpen(true);
        return;
      }
      setIsCreateDropOpen(true);
    };
    $[12] = canCreateDrop;
    $[13] = submissionExperience;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const onCreateDrop = t7;
  const viewPreferenceKey = `waveViewMode_${wave.id}`;
  const defaultViewMode = isMemesWave ? "grid" : "list";
  const [viewMode, setViewMode] = useLocalPreference(viewPreferenceKey, defaultViewMode, isLeaderboardViewMode);
  const sortPreferenceKey = `waveSortMode_${wave.id}`;
  let t8;
  if ($[15] !== isCurationWave) {
    t8 = value => value === WaveDropsLeaderboardSort.RANK || value === WaveDropsLeaderboardSort.RATING_PREDICTION || value === WaveDropsLeaderboardSort.TREND || value === WaveDropsLeaderboardSort.MY_REALTIME_VOTE || value === WaveDropsLeaderboardSort.CREATED_AT || isCurationWave && value === WaveDropsLeaderboardSort.PRICE;
    $[15] = isCurationWave;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const [sort, setSort] = useLocalPreference(sortPreferenceKey, WaveDropsLeaderboardSort.RANK, t8);
  let t9;
  bb0: {
    const hasPriceFilter = typeof minPrice === "number" || typeof maxPrice === "number";
    if (isCurationWave && (hasPriceFilter || sort === WaveDropsLeaderboardSort.PRICE)) {
      t9 = "ETH";
      break bb0;
    }
    t9 = undefined;
  }
  const priceCurrency = t9;
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = t11 => {
      const {
        minPrice: nextMinPrice,
        maxPrice: nextMaxPrice
      } = t11;
      setMinPrice(nextMinPrice);
      setMaxPrice(nextMaxPrice);
    };
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const updatePriceRange = t10;
  let t11;
  bb1: {
    if (!isMemesWave) {
      t11 = viewMode;
      break bb1;
    }
    if (viewMode === "grid_content_only") {
      t11 = "grid";
      break bb1;
    }
    t11 = viewMode;
  }
  const effectiveViewMode = t11;
  let leaderboardContent;
  if (effectiveViewMode === "list") {
    let t12;
    if ($[18] !== maxPrice || $[19] !== minPrice || $[20] !== onCreateDrop || $[21] !== onDropClick || $[22] !== priceCurrency || $[23] !== sort || $[24] !== wave) {
      t12 = <WaveLeaderboardDrops wave={wave} sort={sort} onDropClick={onDropClick} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} onCreateDrop={onCreateDrop} />;
      $[18] = maxPrice;
      $[19] = minPrice;
      $[20] = onCreateDrop;
      $[21] = onDropClick;
      $[22] = priceCurrency;
      $[23] = sort;
      $[24] = wave;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    leaderboardContent = t12;
  } else {
    if (!isMemesWave) {
      const t12 = effectiveViewMode === "grid" ? "compact" : "content_only";
      let t13;
      if ($[26] !== maxPrice || $[27] !== minPrice || $[28] !== onDropClick || $[29] !== priceCurrency || $[30] !== sort || $[31] !== t12 || $[32] !== wave) {
        t13 = <WaveLeaderboardGrid wave={wave} sort={sort} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} mode={t12} onDropClick={onDropClick} />;
        $[26] = maxPrice;
        $[27] = minPrice;
        $[28] = onDropClick;
        $[29] = priceCurrency;
        $[30] = sort;
        $[31] = t12;
        $[32] = wave;
        $[33] = t13;
      } else {
        t13 = $[33];
      }
      leaderboardContent = t13;
    } else {
      let t12;
      if ($[34] !== maxPrice || $[35] !== minPrice || $[36] !== onDropClick || $[37] !== priceCurrency || $[38] !== sort || $[39] !== wave) {
        t12 = <WaveLeaderboardGallery wave={wave} sort={sort} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} onDropClick={onDropClick} />;
        $[34] = maxPrice;
        $[35] = minPrice;
        $[36] = onDropClick;
        $[37] = priceCurrency;
        $[38] = sort;
        $[39] = wave;
        $[40] = t12;
      } else {
        t12 = $[40];
      }
      leaderboardContent = t12;
    }
  }
  let t12;
  if ($[41] !== wave) {
    t12 = <WaveLeaderboardTime wave={wave} />;
    $[41] = wave;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== setViewMode) {
    t13 = mode => setViewMode(mode);
    $[43] = setViewMode;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== setSort) {
    t14 = s => setSort(s);
    $[45] = setSort;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  const t15 = isCurationWave ? updatePriceRange : undefined;
  let t16;
  if ($[47] !== effectiveViewMode || $[48] !== maxPrice || $[49] !== minPrice || $[50] !== onCreateDrop || $[51] !== sort || $[52] !== t13 || $[53] !== t14 || $[54] !== t15 || $[55] !== wave) {
    t16 = <div className="tw-sticky tw-top-0 tw-z-30 tw-bg-black tw-py-4"><WaveLeaderboardHeader wave={wave} viewMode={effectiveViewMode} sort={sort} onViewModeChange={t13} onCreateDrop={onCreateDrop} onSortChange={t14} minPrice={minPrice} maxPrice={maxPrice} onPriceRangeChange={t15} /></div>;
    $[47] = effectiveViewMode;
    $[48] = maxPrice;
    $[49] = minPrice;
    $[50] = onCreateDrop;
    $[51] = sort;
    $[52] = t13;
    $[53] = t14;
    $[54] = t15;
    $[55] = wave;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  let t17;
  if ($[57] !== showToggleableDropInput || $[58] !== wave) {
    t17 = showToggleableDropInput && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: 0.2,
      ease: "easeInOut"
    }}><WaveDropCreate wave={wave} onCancel={() => {
        if (mountedRef.current) {
          setIsCreateDropOpen(false);
        }
      }} onSuccess={() => {
        if (mountedRef.current) {
          setIsCreateDropOpen(false);
        }
      }} /></motion.div>;
    $[57] = showToggleableDropInput;
    $[58] = wave;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  let t18;
  if ($[60] !== t17) {
    t18 = <AnimatePresence>{t17}</AnimatePresence>;
    $[60] = t17;
    $[61] = t18;
  } else {
    t18 = $[61];
  }
  let t19;
  if ($[62] !== isMemesCreateOpen || $[63] !== submissionExperience || $[64] !== wave) {
    t19 = submissionExperience === WaveSubmissionExperience.MEMES_LEGACY && isMemesCreateOpen && <MemesArtSubmissionModal isOpen={isMemesCreateOpen} wave={wave} onClose={() => setIsMemesCreateOpen(false)} />;
    $[62] = isMemesCreateOpen;
    $[63] = submissionExperience;
    $[64] = wave;
    $[65] = t19;
  } else {
    t19 = $[65];
  }
  let t20;
  if ($[66] !== isCurationDropModalOpen || $[67] !== submissionExperience || $[68] !== wave) {
    t20 = submissionExperience === WaveSubmissionExperience.CURATION_LEGACY && isCurationDropModalOpen && <WaveLeaderboardCurationDropModal isOpen={isCurationDropModalOpen} wave={wave} onClose={() => setIsCurationDropModalOpen(false)} />;
    $[66] = isCurationDropModalOpen;
    $[67] = submissionExperience;
    $[68] = wave;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  let t21;
  if ($[70] !== isCreateDropOpen || $[71] !== submissionExperience || $[72] !== wave) {
    t21 = submissionExperience === WaveSubmissionExperience.QUORUM_PROPOSAL && isCreateDropOpen && <WaveDropCreate wave={wave} onCancel={() => setIsCreateDropOpen(false)} onSuccess={() => setIsCreateDropOpen(false)} />;
    $[70] = isCreateDropOpen;
    $[71] = submissionExperience;
    $[72] = wave;
    $[73] = t21;
  } else {
    t21 = $[73];
  }
  let t22;
  if ($[74] !== leaderboardContent || $[75] !== t18 || $[76] !== t19 || $[77] !== t20 || $[78] !== t21) {
    t22 = <div className="tw-min-w-0 tw-pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)]">{t18}{t19}{t20}{t21}{leaderboardContent}</div>;
    $[74] = leaderboardContent;
    $[75] = t18;
    $[76] = t19;
    $[77] = t20;
    $[78] = t21;
    $[79] = t22;
  } else {
    t22 = $[79];
  }
  let t23;
  if ($[80] !== leaderboardViewStyle || $[81] !== t12 || $[82] !== t16 || $[83] !== t22) {
    t23 = <div className="tw-w-full tw-min-w-0 tw-flex tw-flex-col tw-rounded-t-xl tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 tw-overflow-x-hidden tw-flex-grow tw-px-2 sm:tw-px-4" style={leaderboardViewStyle}>{t12}{t16}{t22}</div>;
    $[80] = leaderboardViewStyle;
    $[81] = t12;
    $[82] = t16;
    $[83] = t22;
    $[84] = t23;
  } else {
    t23 = $[84];
  }
  return t23;
};
export default MyStreamWaveLeaderboard;